"use client";
import React, {
  useEffect,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";
import Cookies from "js-cookie";
import { useMutation } from "@tanstack/react-query";
import { CONFIG } from "../util/config";

import {
  decodeAccessToken,
  getAccessToken,
  getLoggedinUser,
  isTokenValid,
  useAuth,
  useIpDetails,
} from "../util";
import { useAppDispatch, useAppSelector } from "../../components/slice/hooks";
import {
  getActiveUser,
  logInUser,
  logOutUser,
  logTempToken,
} from "../../components/slice/auth-slice";

const AuthContext = React.createContext();
AuthContext.displayName = "AuthContext";

function AuthProvider(props) {
  const { data: ipDetails, isSuccess, isError } = useIpDetails();
  const dispatch = useAppDispatch();
  const { tokenData } = useAppSelector(getActiveUser);
  const { logUserOut } = useAuth();
  const hasBootstrapped = useRef(false);
  const refreshTimeout = useRef(null);

  const { data: publicTokenData, mutate: fetchGuestToken } = useMutation(
    getAccessToken,
    {
      onSuccess: (res) => {
        const token = res?.data?.token;
        const tokenExpiryDate = res?.data?.tokenExpiryDate;
        if (token) {
          dispatch(logTempToken({ token, tokenExpiryDate }));
        }
      },
      onError: (error) => {
        console.log({ error });
      },
    }
  );

  const { mutate: decodeToken } = useMutation(decodeAccessToken, {
    onSuccess: (response) => {
      const payload = response?.data?.payload;
      const token = response?.data?.token;
      const tokenExpiryDate = response?.data?.tokenExpiryDate;
      if (payload?.tokenType === "guest") {
        dispatch(logTempToken({ token, tokenExpiryDate }));
      }
    },
    onError: async (error) => {
      dispatch(logOutUser());
      if (isSuccess) {
        const { token, tokenExpiryDate } = await logUserOut(ipDetails);
        if (token) {
          dispatch(logTempToken({ token, tokenExpiryDate }));
        }
      }
    },
  });

  useEffect(() => {
    if (!hasBootstrapped.current && (isSuccess || isError)) {
      hasBootstrapped.current = true;

      const loggedInUser = getLoggedinUser();
      const token = loggedInUser?.token || Cookies.get("token");
      const tokenExpiryDate =
        loggedInUser?.tokenExpiryDate || Cookies.get("tokenExpiryDate");

      if (loggedInUser && isTokenValid(token, tokenExpiryDate)) {
        dispatch(logInUser({ loggedInUser, authenticated: true, token }));
      }

      if (token && isTokenValid(token, tokenExpiryDate)) {
        decodeToken(token);
      } else {
        fetchGuestToken(isSuccess ? ipDetails : null);
      }
    }
  }, [isSuccess, isError]);

  // Automatic token refresh
  useEffect(() => {
    if (!tokenData?.tokenExpiryDate || !tokenData?.token) return;

    const expiryDate = new Date(tokenData.tokenExpiryDate);
    const now = new Date();
    const timeUntilExpiry = expiryDate - now;
    const refreshTime = timeUntilExpiry - 60 * 1000; // 1 min before expiry

    if (refreshTime > 0) {
      refreshTimeout.current = setTimeout(() => {
        decodeToken(tokenData.token);
      }, refreshTime);
    }

    return () => {
      if (refreshTimeout.current) {
        clearTimeout(refreshTimeout.current);
      }
    };
  }, [tokenData?.token, tokenData?.tokenExpiryDate]);

  const tokenInfo = useMemo(
    () => ({
      token: tokenData?.token || publicTokenData?.data?.token,
      tokenExpiryDate:
        tokenData?.tokenExpiryDate || publicTokenData?.data?.tokenExpiryDate,
    }),
    [publicTokenData, tokenData]
  );

  return <AuthContext.Provider value={tokenInfo} {...props} />;
}

function useToken() {
  let { token, tokenExpiryDate } = useTokenData();
  if (!token || !tokenExpiryDate) {
    token = Cookies.get("token");
    tokenExpiryDate = Cookies.get("tokenExpiryDate");
  }
  if (token && tokenExpiryDate) {
    tokenExpiryDate = new Date(tokenExpiryDate);
    const currentDate = new Date();
    if (tokenExpiryDate < currentDate) {
      token = null;
      tokenExpiryDate = null;
    }
  }

  return {
    token,
    tokenExpiryDate,
  };
}

function useTokenData() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error(`useTokenData must be used within a AuthProvider`);
  }
  return context;
}

function useClient() {
  const { token } = useToken();

  return useCallback(
    (
      endpoint,
      { method, headers: customHeaders, data, ...customConfig } = {}
    ) => {
      // If the method is not explicitly set, use POST if data is provided, otherwise use GET
      const finalMethod = method || (data ? "POST" : "GET");

      const config = {
        method: finalMethod,
        body: data ? JSON.stringify(data) : undefined,
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": data ? "application/json" : undefined,
          ...(customHeaders || {}),
        },
        ...customConfig,
      };

      return fetch(`${CONFIG.apiUrl}/${endpoint}`, config).then(
        async (response) => {
          const responseData = await response.json();
          if (response.ok) {
            return responseData;
          } else {
            return Promise.reject(responseData);
          }
        }
      );
    },
    [token]
  );
}

export { AuthProvider, AuthContext, useClient, useTokenData, useToken };
